// eslint-disable-next-line no-restricted-imports
import "./fonts.css";

import { ScopedCssBaseline } from "@mui/material";
import { ThemeProvider, type ThemeProviderProps } from "@mui/system";
import { useThemeMode } from "@src/appV2/UserPreferences/UserPreferencesProvider";

import { berlinRebrandTheme, berlinRebrandThemeDark } from "./theme";

/**
 * BerlinThemeProvider sets up the Berlin design system theme with proper font loading.
 *
 * Features:
 * - Loads Circular font family from local assets (WOFF2 format) - it is done via fonts.css file.
 *   It doesn't use GlobalStyles component as it strips css comments, which should include legal disclaimer for the font.
 * - Applies CssBaseline for consistent cross-browser styling
 * - Provides theme context to all child components
 *
 * Font Files:
 * - Uses CircularXXWeb-Medium.woff2 (variable font supporting multiple weights)
 * - Falls back to WOFF format for older browsers
 *
 * @see https://mui.com/material-ui/customization/theming/
 * @see https://mui.com/material-ui/react-css-baseline/
 */
interface BerlinThemeProviderProps extends Omit<ThemeProviderProps, "theme"> {
  /**
   * When true, the ScopedCssBaseline wrapper fills its parent's height. Opt in
   * from page-level wrappers that host a PageLayout so the flex/height chain
   * propagates correctly. Leave false (default) for dialogs, sheets, and other
   * self-sizing consumers.
   */
  fullHeight?: boolean;
}

export function BerlinThemeProvider(props: BerlinThemeProviderProps) {
  const { children, fullHeight = false } = props;

  const { themeMode } = useThemeMode();
  const theme = themeMode === "dark" ? berlinRebrandThemeDark : berlinRebrandTheme;

  return (
    <ThemeProvider theme={theme}>
      {/* Using ScopedCssBaseline to avoid some styles being applied globally
      Read more here: https://mui.com/material-ui/react-css-baseline/#scoping-on-children */}
      <ScopedCssBaseline
        sx={(theme) => ({
          // ScopedCssBaseline defaults its root background to background.default
          // (#FEFCF8). Pages render on the white page surface, so paint the
          // baseline with background.page to keep nested providers white too.
          backgroundColor: theme.palette.background.page,
          ...(fullHeight ? { height: "100%", minHeight: 0 } : {}),
        })}
      >
        {children}
      </ScopedCssBaseline>
    </ThemeProvider>
  );
}
